import {BasicDataTable, BasicDataTableConfig, DataTablesDataSource, TypedColumnSettings} from "@/shared/ui/basicDataTable"
import {
	AssignmentPanel,
	AssignmentPanelConfiguration,
	BaseAssignmentTable,
	UserSelectTable
} from "./assignmentPanel";
import {htmlEscape} from "../utils/string";
import {BaseTree} from "../ui/baseTree";
import {AssignmentTree} from "./assignmentTree";
import {SecurityStatus} from "@/shared/securityStatus";
import {formatDecimalNumber, parseDecimalNumber} from "@/shared/utils/datefunctions";
import {AssignedGroup, AssignedUser, Group} from "@/shared/timr";
import {BudgetPlanningType, RecordingLockType} from "@/shared/timrTypes";

export type UserGroupAssignmentEntry = AssignedUser | AssignedGroup;

export class UserGroupAssignmentPanel extends AssignmentPanel<UserGroupAssignmentEntry, AssignedUser> {
	private assignedTable: BaseAssignmentTable<UserGroupAssignmentEntry>;
	private userSelectTable: BaseAssignmentTable<AssignedUser>;
	private groupAssignmentTree?: AssignmentTree<Group>;
	protected assignmentOverviewTable: AssignmentOverviewTable;

	public constructor(config: AssignmentPanelConfiguration<UserGroupAssignmentEntry, AssignedUser>) {
		super(config, ((t1, t2) => t1.id === t2.id && t1.elementType === t2.elementType));

		this.assignedTable = this.createAssignedComponent(this.config.panelElement.find("table.assigned-element"));
		this.userSelectTable = this.createSelectComponent(this.config.panelElement.find("table.select-element"));
		this.assignmentOverviewTable = this.createAssignmentOverviewTable(this.config.panelElement.find("table.overview-element"));

		if (SecurityStatus.isCorporateFeaturesAllowed()) {
			this.groupAssignmentTree = new AssignmentTree(new BaseTree({
				treeElement: this.config.panelElement.find("div.select-element"),
				loadRootNodesURL: "admin/loadAllGroups.json",
				loadSubNodesURL: "admin/loadSubGroups.json",
				filterNodesURL: "reports/filterGroups.json",
				getRequestData: () => null,
				isNodeSelectable: () => false
			}), (node) => {
				this.assign(node.data as UserGroupAssignmentEntry);
			});
		}

		$(this.userSelectTable).on(BaseAssignmentTable.EVENT_ENTRY_REMOVED, (event, user: UserGroupAssignmentEntry) => {
			user.elementType = "USER";

			this.assign(user);
		});

		$(this.assignedTable).on(BaseAssignmentTable.EVENT_ENTRY_REMOVED, (event, userOrGroup: UserGroupAssignmentEntry) => {
			this.unassign(userOrGroup);
		});
	}

	public filterSelection(filter: string) {
		this.userSelectTable.searchOfflineData(filter);
		this.groupAssignmentTree?.getBaseTree().queryTree(filter);
		this.assignedTable.searchOfflineData(filter);
		this.assignmentOverviewTable.searchOfflineData(filter);
	}

	protected updateEditView() {
		this.assignedTable.reload();
		this.userSelectTable.reload();

		const assignedGroups = this.getAssigned().filter(userOrGroup => userOrGroup.elementType === "GROUP");
		this.groupAssignmentTree?.setAssigned(assignedGroups);
	}

	protected updateOverview() {
		this.assignmentOverviewTable.reload();
	}

	private getUsersForSelection(): PromiseLike<AssignedUser[]> {
		const allAssigned = this.getAssigned();
		const assignedUsers = allAssigned.filter(userOrGroup => userOrGroup.elementType === "USER");

		return this.config.selectSourceProvider().then((users) => {
			return users.filter(u => {
				return !assignedUsers.some(v => v.id === u.id);
			});
		});
	}

	public setScrollHeight(height: number) {
		this.assignedTable.setScrollHeight(height);
		this.userSelectTable.setScrollHeight(height);
		this.groupAssignmentTree?.getBaseTree().setMaxHeight(height + 5);
		this.assignmentOverviewTable.setScrollHeight(height);

		this.assignedTable.redrawCurrentPage();
		this.userSelectTable.redrawCurrentPage();
		this.assignmentOverviewTable.redrawCurrentPage();
	}

	protected createSelectComponent(element: JQuery): BaseAssignmentTable<AssignedUser> {
		return new UserSelectTable(() => this.getUsersForSelection(), {
			element: element,
			orderable: false,
			allowExpand: false,
			clampNote: false
		});
	}

	protected createAssignedComponent(element: JQuery): BaseAssignmentTable<UserGroupAssignmentEntry> {
		return new MixedUserGroupAssignedTable(() => this.getAssigned(), {
			element: element,
			orderable: false,
			allowExpand: false,
			clampNote: false
		});
	}

	protected createAssignmentOverviewTable(element: JQuery): AssignmentOverviewTable {
		return new AssignmentOverviewTable(() => this.getAssigned(), {
			element: element,
			orderable: false,
			allowExpand: false,
			clampNote: false
		});
	}
}

class MixedUserGroupAssignedTable extends BaseAssignmentTable<UserGroupAssignmentEntry> {
	constructor(dataSource: DataTablesDataSource<UserGroupAssignmentEntry>,
				config: BasicDataTableConfig) {
		super(dataSource, config);
	}

	protected getColumnSettings(): TypedColumnSettings<UserGroupAssignmentEntry>[] {
		return [
			{
				data: "elementType",
				orderable: true,
				render: (d, t, r, m) => this.renderTypeIcon(d, t, r, m),
				width: BasicDataTable.COLUMN_SIZE_STATUS_ICON
			},
			{
				orderable: true,
				render: (d: void, t: string, r: UserGroupAssignmentEntry, m: DataTables.CellMetaSettings) => this.renderNameText(d, t, r, m)
			},
			{
				className: "no-padding-top no-padding-bottom no-padding-left",
				orderable: false,
				render: () => this.renderControlButton("changeAssignmentButton", messages["group.user.remove"], "fa-minus"),
				width: BasicDataTable.COLUMN_SIZE_QUICK_SWITCH_BUTTON
			}
		];
	}

	protected getDefaultOrder(): (string | number)[][] {
		return [[0, "asc"]];
	}

	protected renderTypeIcon(data: string, type: string, row: UserGroupAssignmentEntry, meta: DataTables.CellMetaSettings): string {
		if (type === "display") {
			const icon = data === "USER" ? "fa-user" : "fa-users";
			const title = data === "USER" ? messages["record.user"] : messages["record.group"];
			return `<i class="fal ${icon}" title="${title}"></i>`;
		} else {
			return data;
		}
	}

	protected renderNameText(data: unknown, type: string, row: UserGroupAssignmentEntry, meta: DataTables.CellMetaSettings) {
		const classes = row.autoAssigned === true ? ` class="text-grey"` : "";

		const text = row.elementType === "USER" ? row.fullname : row.name;

		return `<span ${classes}>${htmlEscape(text)}</span>`;
	}
}

class AssignmentOverviewTable extends BasicDataTable<UserGroupAssignmentEntry> {
	protected getColumnSettings(): TypedColumnSettings<UserGroupAssignmentEntry>[] {
		return [
			{
				data: "elementType",
				orderable: false,
				render: (d, t, r, m) => this.renderTypeIcon(d, t, r, m),
				width: BasicDataTable.COLUMN_SIZE_STATUS_ICON
			},
			{
				orderable: true,
				render: (d: void, t: string, r: UserGroupAssignmentEntry, m: DataTables.CellMetaSettings) => this.renderNameText(d, t, r, m)
			}
		];
	}

	protected getDefaultOrder(): (string | number)[][] {
		return [[0, "asc"]];
	}

	protected renderTypeIcon(data: string, type: string, row: UserGroupAssignmentEntry, meta: DataTables.CellMetaSettings): string {
		if (type === "display") {
			const icon = data === "USER" ? "fa-user" : "fa-users";
			const title = data === "USER" ? messages["record.user"] : messages["record.group"];
			return `<i class="fal ${icon}" title="${title}"></i>`;
		} else {
			return data;
		}
	}

	protected renderNameText(data: unknown, type: string, row: UserGroupAssignmentEntry, meta: DataTables.CellMetaSettings) {
		const classes = row.autoAssigned === true ? ` class="text-grey"` : "";

		const text = row.elementType === "USER" ? row.fullname : row.name;

		return `<span ${classes}>${htmlEscape(text)}</span>`;
	}

	protected createDataTableConfig(): DataTables.Settings {
		return {
			dom: `rt`,
			paging: false,
			scrollY: "300px",
			scrollCollapse: true,
			searching: true,
			rowCallback: (row, data, index) => {
				const assignedUser = data as AssignedUser;
				if (assignedUser.disabled) {
					$(row).addClass("archived");
				}
			},
		};
	}
}

export class TaskUserGroupAssignmentPanel extends UserGroupAssignmentPanel {

	declare protected assignmentOverviewTable: TaskAssignmentOverviewTable;

	constructor(config: AssignmentPanelConfiguration<UserGroupAssignmentEntry, AssignedUser>, private onHoursPlannedUpdated: () => void) {
		super(config);
	}

	protected createAssignmentOverviewTable(element: JQuery): TaskAssignmentOverviewTable {
		return new TaskAssignmentOverviewTable(() => this.getAssigned(), {
			element: element,
			orderable: false,
			allowExpand: false,
			clampNote: false
		}, () => this.onHoursPlannedUpdated());
	}

	public setBudgetPlanningTypeInformation(budgetPlanningType: BudgetPlanningType, showBudgetFeature: boolean) {
		this.assignmentOverviewTable.setBudgetPlanningTypeInformation(budgetPlanningType, showBudgetFeature);
	}
}

class TaskAssignmentOverviewTable extends AssignmentOverviewTable {
	private static readonly COLUM_SIZE_INPUT = "150px";
	private showPlanning: boolean = false;
	private showBudgetFeature: boolean = false;
	private budgetPlanningType: BudgetPlanningType;

	constructor(dataSource: DataTablesDataSource<UserGroupAssignmentEntry>, config: BasicDataTableConfig, private onHoursPlannedUpdated: () => void) {
		super(dataSource, config);

		this.config.element.on("change", `input[name="recordingLock"]`, (event) => {
			const input = $(event.target as HTMLElement);
			const record = this.getRowData(input);

			if (record.elementType === "USER") {
				record.recordingLock = input.is(":checked")
					? RecordingLockType.LockedManually
					: RecordingLockType.UnlockedManually;
			}
		});

		this.config.element.on("change", `input[name="hoursPlanned"]`, (event) => {
			const input = $(event.target as HTMLElement);
			const record = this.getRowData(input);

			if (record.elementType === "USER") {
				record.hoursPlanned = parseDecimalNumber(input.val() as string);
				record.hoursPlannedManually = true;
				this.onHoursPlannedUpdated();
			}
		});

		this.config.element.on("click", `button[name="resetBudget"]`, (event) => {
			const input = $(event.target as HTMLElement);
			const record = this.getRowData(input);

			if (record.elementType === "USER") {
				record.hoursPlanned = null;
				record.hoursPlannedManually = false;
				this.onHoursPlannedUpdated();
			}
		});

		this.config.element.on("click", `button[name="hoursPlannedManually"]`, (event) => {
			const button = $(event.target as HTMLElement);
			const record = this.getRowData(button);

			if (record.elementType === "USER") {
				record.hoursPlanned = 0;
				record.hoursPlannedManually = true;
				this.onHoursPlannedUpdated();
			}
		});
	}

	public setBudgetPlanningTypeInformation(budgetPlanningType: BudgetPlanningType, showBudgetFeature: boolean) {
		this.budgetPlanningType = budgetPlanningType;
		this.showBudgetFeature = showBudgetFeature;

		this.showPlanning = showBudgetFeature && budgetPlanningType !== BudgetPlanningType.None && budgetPlanningType !== BudgetPlanningType.Inherited;

		this.dataTable.columns([2/*hoursPlanned*/, 4/*hourlyRate warning*/]).visible(this.showPlanning, false);
		this.dataTable.columns([3/*recordingLock*/]).visible(this.showBudgetFeature, false);
		this.dataTable.columns.adjust().draw("page");
	}

	protected getColumnSettings(): TypedColumnSettings<UserGroupAssignmentEntry>[] {
		return [
			{
				data: "elementType",
				orderable: true,
				render: (d, t, r, m) => this.renderTypeIcon(d, t, r, m),
				width: BasicDataTable.COLUMN_SIZE_STATUS_ICON
			},
			{
				orderable: true,
				render: (d: void, t: string, r: UserGroupAssignmentEntry, m: DataTables.CellMetaSettings) => this.renderNameText(d, t, r, m)
			},
			{
				data: "hoursPlanned",
				orderable: false,
				width: TaskAssignmentOverviewTable.COLUM_SIZE_INPUT,
				visible: SecurityStatus.isBudgetsAllowed() && this.showPlanning,
				render: (d, t, r, m) => this.renderHoursPlanned(d, t, r, m)
			},
			{
				data: "recordingLock",
				orderable: false,
				width: BasicDataTable.COLUMN_SIZE_CHECKBOX,
				visible: SecurityStatus.isBudgetsAllowed() && this.showBudgetFeature,
				render: (d, t, r, m) => this.renderLockCheckbox(d, t, r, m)
			},
			{
				data: "hourlyRate",
				orderable: false,
				width: BasicDataTable.COLUMN_SIZE_STATUS_ICON,
				render: (d, t, r, m) => this.renderHourlyRateWarning(d, t, r, m)
			}
		];
	}

	private renderLockCheckbox(data: RecordingLockType, type: string, row: UserGroupAssignmentEntry, meta: DataTables.CellMetaSettings) {
		if (row.elementType === "USER") {
			const checked = data === RecordingLockType.LockedManually || data === RecordingLockType.LockedAutomatically;
			return `<input type="checkbox" name="recordingLock" ${checked ? "checked" : ""}>`;
		} else {
			return "";
		}
	}

	private renderHoursPlanned(data: number, type: string, row: UserGroupAssignmentEntry, meta: DataTables.CellMetaSettings) {
		if (row.elementType === "USER") {
			const hoursPlanned = row.hoursPlanned;
			if (hoursPlanned != null) {
				return `
						<div class="input-group input-group-xs">
							<input type="text" name="hoursPlanned" class="form-control" value="${formatDecimalNumber(data)}">
							<span class="input-group-btn">
       							<button class="btn btn-icon btn-info" type="button" name="resetBudget">
       								<i class="far fa-times"></i>
								</button>
      						</span>
						</div>
					`;
			} else {
				return `
						<button type="button" class="btn btn-link-primary" name="hoursPlannedManually">
							${messages["task.tab.assignedTo.fixedBudget"]}
						</button>
					`;
			}
		} else {
			return "";
		}
	}

	// data is the numeric rate for users and a null|true flag for groups; null means "rate not fully set" for both.
	private renderHourlyRateWarning(data: number | boolean, type: string, row: UserGroupAssignmentEntry, meta: DataTables.CellMetaSettings) {
		if (data == null && this.budgetPlanningType === BudgetPlanningType.UserHourlyRate) {
			const title = messages[row.elementType === "USER" ? "user.hourlyRate.unspecified" : "group.hourlyRate.unspecified"];
			return `<i class="far fa-exclamation-triangle text-warning" title="${title}"></i>`;
		} else {
			return "";
		}
	}
}
